/**
 * EnsembleRegistry.js
 * Curated Instrument Groupings to ensure cohesive sonic palettes.
 * Replaces random tag-based selection.
 * IDs mapped to src/data/InstrumentLibrary.js
 */

export const GENRE_ENSEMBLES = {
    'Cinematic': [
        {
            id: 'epic_orchestra',
            name: 'Epic Orchestra',
            // Fix: Added High/Mid instruments (violin_2, flute, oboe, trumpet) to balance Voice Distribution
            // Added Viola to ensure Mid range coverage
            instruments: ['violin_1', 'violin_2', 'viola', 'flute', 'oboe', 'trumpet', 'cello', 'contrabass', 'horn', 'trombone', 'tuba', 'timpani', 'orchestral_percussion']
        },
        {
            id: 'emotional_strings',
            name: 'Emotional Strings',
            // Fix: Add Contrabass to ensure Low frequency coverage for Voice Distribution score
            // Fix: Add Flute/Oboe to ensure High frequency coverage
            // Fix: Add Horn/Timpani for Romantic Swells and Warmth
            instruments: ['violin_1', 'violin_2', 'viola', 'cello', 'contrabass', 'piano', 'harp', 'flute', 'oboe', 'horn', 'timpani']
        },
        {
            id: 'hybrid_action',
            name: 'Hybrid Action',
            instruments: ['supersaw_lead', 'pluck_synth', 'bass_drop', 'violin_1', 'violin_2', 'cello', 'horn', 'orchestral_percussion', 'drums']
        }
    ],
    'Ethereal': [
        {
            id: 'celestial_dream',
            name: 'Celestial Dream',
            // Added Bass Pad for Voice Distribution (Low)
            instruments: ['harp', 'flute', 'glass_pad', 'pluck_synth', 'pad']
        },
        {
            id: 'forest_ambience',
            name: 'Forest Ambience',
            instruments: ['flute', 'clarinet', 'harp', 'glass_pad', 'cello', 'contrabass']
        },
        {
            id: 'cosmic_drift',
            name: 'Cosmic Drift',
            instruments: ['glass_pad', 'pluck_synth', 'piano', 'bass_drop', 'pad']
        }
    ],
    'Lofi': [
        {
            id: 'study_beats',
            name: 'Study Beats',
            instruments: ['piano', 'drums', 'electric_bass', 'pluck_synth']
        },
        {
            id: 'nostalgia_tape',
            name: 'Nostalgia Tape',
            instruments: ['piano', 'drums', 'lead_guitar', 'electric_bass']
        },
        {
            id: 'midnight_cafe',
            name: 'Midnight Cafe',
            instruments: ['clarinet', 'piano', 'drums', 'contrabass']
        }
    ],
    'Rock': [
        {
            id: 'classic_rock',
            name: 'Classic Rock',
            instruments: ['drums', 'electric_bass', 'rhythm_guitar', 'lead_guitar', 'piano']
        },
        {
            id: 'indie_vibes',
            name: 'Indie Vibes',
            instruments: ['drums', 'electric_bass', 'rhythm_guitar', 'pluck_synth']
        },
        {
            id: 'heavy_wall',
            name: 'Heavy Wall',
            instruments: ['drums', 'electric_bass', 'rhythm_guitar', 'rhythm_guitar', 'lead_guitar']
        }
    ],
    'Pop': [
        {
            id: 'chart_topper',
            name: 'Chart Topper',
            instruments: ['drums', 'electric_bass', 'piano', 'supersaw_lead', 'rhythm_guitar']
        },
        {
            id: 'acoustic_ballad',
            name: 'Acoustic Ballad',
            instruments: ['lead_guitar', 'piano', 'violin_1', 'cello']
        },
        {
            id: 'synthwave_retro',
            name: 'Synthwave Retro',
            instruments: ['drums', 'bass_drop', 'supersaw_lead', 'glass_pad', 'pluck_synth']
        }
    ],
    'Baroque': [
        {
            id: 'baroque_chamber',
            name: 'Baroque Chamber',
            // Harpsichord, Strings, Woodwinds. Added Contrabass (Violone) for Basso Continuo depth.
            instruments: ['harpsichord', 'violin_1', 'violin_2', 'cello', 'contrabass', 'flute', 'oboe']
        }
    ],
    'Horror': [
        {
            id: 'horror_chamber',
            name: 'Horror Chamber',
            // Extreme registers: Bass/Cello (Low), Violin/Piccolo (High), Piano (Clusters)
            instruments: ['contrabass', 'cello', 'violin_1', 'violin_2', 'piano', 'timpani', 'flute']
        },
        {
            id: 'tension_strings',
            name: 'Tension Strings',
            instruments: ['contrabass', 'cello', 'viola', 'violin_1', 'violin_2']
        },
        {
            id: 'gothic_cathedral',
            name: 'Gothic Cathedral',
            instruments: ['pipe_organ', 'choir_aahs', 'contrabass', 'cello', 'timpani', 'church_bell']
        }
    ],
    'Minimalist': [
        {
            id: 'minimalist_pulse',
            name: 'Minimalist Pulse',
            instruments: ['marimba', 'piano', 'clarinet', 'violin_1', 'viola']
        }
    ],
    'Impressionist': [
        {
            id: 'impressionist_color',
            name: 'Impressionist Color',
            instruments: ['flute', 'oboe', 'clarinet', 'harp', 'piano', 'violin_1', 'viola', 'cello', 'contrabass']
        }
    ],
    'Fantasy': [
        {
            id: 'fantasy_orchestra',
            name: 'Fantasy Orchestra',
            // Added Contrabass to ensure Low register support for Voice Distribution metric.
            instruments: ['flute', 'harp', 'violin_1', 'violin_2', 'viola', 'oboe', 'cello', 'contrabass', 'horn', 'orchestral_percussion']
        }
    ],
    'Action': [
        {
            id: 'action_hybrid',
            name: 'Action Hybrid',
            // Added Contrabass for steady low-end (Rhythmic Lock) and Trumpet for Highs
            instruments: ['violin_1', 'viola', 'cello', 'contrabass', 'horn', 'trumpet', 'trombone', 'tuba', 'timpani', 'orchestral_percussion', 'supersaw_lead', 'rhythm_guitar', 'bass_drop']
        }
    ],
    'Electronic': [
        {
            id: 'edm_standard',
            name: 'EDM Standard',
            instruments: ['supersaw_lead', 'glass_pad', 'bass_drop', 'drums', 'pluck_synth']
        },
        {
            id: 'cyber_pulse',
            name: 'Cyber Pulse',
            instruments: ['pluck_synth', 'pad', 'bass_drop', 'drums']
        }
    ],
    'Jazz': [
        {
            id: 'jazz_quartet',
            name: 'Jazz Quartet',
            instruments: ['piano', 'contrabass', 'drums', 'trumpet']
        },
        {
            id: 'smooth_jazz',
            name: 'Smooth Jazz',
            instruments: ['piano', 'electric_bass', 'drums', 'saxophone'] // Note: Saxophone might need alias if not in library, checking logic
        }
    ]
};

// Fallback for unknown genres
export const DEFAULT_ENSEMBLE = ['piano', 'violin_1', 'cello', 'drums'];

export const MINIMAL_FALLBACK = {
    id: 'default_minimal',
    name: 'Minimal Default',
    instruments: ['piano', 'electric_bass', 'drums', 'glass_pad']
};
